import type { ProfileId } from '../../shared/types/ids';
import type { ChildProfile, FamilyProfile } from '../../shared/types/profiles';
import type { ProfileRepository } from '../interfaces/repositories';
import { projectChildren, projectFamily } from './governed-records';
import type { JsonStore } from './json-store';
import { STORAGE_KEYS } from './keys';
import { toChildProfileDto, toChildProfileListDto } from './persistence-dto';

/**
 * Local profile storage. Profiles hold a nickname, a band and preferences — never a real
 * name requirement, an account, an email or anything biometric (architecture §19).
 *
 * Both directions go through the persistence DTOs: unknown fields are stripped on the way in,
 * and a record that no longer matches its shape reads back as absent.
 */
export function createLocalProfileRepository(store: JsonStore): ProfileRepository {
  const readChildren = (): ChildProfile[] =>
    store.read(STORAGE_KEYS.children, projectChildren) ?? [];

  const writeChildren = (children: ChildProfile[]): void => {
    store.write(STORAGE_KEYS.children, toChildProfileListDto(children));
  };

  return {
    async getFamily(): Promise<FamilyProfile | null> {
      return store.read(STORAGE_KEYS.family, projectFamily);
    },

    async saveFamily(profile: FamilyProfile): Promise<void> {
      const projected = projectFamily(profile);
      if (projected === null) return;
      store.write(STORAGE_KEYS.family, projected);
    },

    async listChildren(): Promise<ChildProfile[]> {
      return readChildren();
    },

    async getChild(id: ProfileId): Promise<ChildProfile | null> {
      return readChildren().find((child) => child.id === id) ?? null;
    },

    async saveChild(profile: ChildProfile): Promise<void> {
      const projected = toChildProfileDto(profile);
      if (projected === null) return;

      const children = readChildren();
      const index = children.findIndex((child) => child.id === projected.id);
      if (index === -1) children.push(projected);
      else children[index] = projected;
      writeChildren(children);
    },

    async deleteChild(id: ProfileId): Promise<void> {
      writeChildren(readChildren().filter((child) => child.id !== id));

      // Keep the family's child list consistent, or the parent zone would show a ghost child.
      const family = store.read(STORAGE_KEYS.family, projectFamily);
      if (family === null) return;
      store.write(STORAGE_KEYS.family, {
        ...family,
        childProfileIds: family.childProfileIds.filter((childId) => childId !== id),
        updatedAt: new Date().toISOString(),
      } satisfies FamilyProfile);
    },
  };
}
